/**
 * DefinitionsCatalogue — one catalogue section (Monthly | Weekly). On screen it
 * reads as a reference chapter: a section heading, then the definitions grouped
 * under anchored pillar sub-headings (the nav TOC jumps to these). The printed
 * §7 S4 pack is unchanged — one artefact, the whole catalogue, nothing paged or
 * collapsed; the weekly section starts a fresh page (printBreakBefore). The
 * `query` filter hides non-matching rows on screen via `hidden` but leaves them
 * `print:block`, so a filtered screen never yields a partial printed pack.
 */

import React from 'react'
import clsx from 'clsx'
import type { Cadence, RegistryMetric } from '../../types/metrics'
import { metricMatches } from '../../content/definitionDocs'
import { pillarGroups } from './catalogueRows'
import DefinitionCard from './DefinitionCard'

interface DefinitionsCatalogueProps {
  testId: string
  title: string
  subtitle: string
  cadence: Cadence
  rows: RegistryMetric[]
  query: string
  printBreakBefore?: boolean
}

function PillarSection({
  pillar,
  metrics,
  cadence,
  query,
  anchorId,
}: {
  pillar: string
  metrics: RegistryMetric[]
  cadence: Cadence
  query: string
  anchorId?: string // only the pillar's first run is a TOC anchor (ids stay unique)
}) {
  const noneMatch = metrics.every((metric) => !metricMatches(metric, query))
  return (
    <div
      id={anchorId}
      className={clsx('scroll-mt-24 space-y-3', noneMatch && 'hidden print:block')}
    >
      <h3 className="break-after-avoid text-[11px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
        {pillar}
      </h3>
      {metrics.map((metric) => (
        <DefinitionCard
          key={metric.id}
          metric={metric}
          cadence={cadence}
          hidden={!metricMatches(metric, query)}
        />
      ))}
    </div>
  )
}

export default function DefinitionsCatalogue({
  testId,
  title,
  subtitle,
  cadence,
  rows,
  query,
  printBreakBefore = false,
}: DefinitionsCatalogueProps): React.JSX.Element {
  return (
    <section
      data-testid={testId}
      className={clsx('space-y-4', printBreakBefore && 'print:break-before-page')}
    >
      <div className="break-after-avoid border-b border-slate-300 pb-1.5 dark:border-slate-700">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
      </div>
      {(() => {
        const anchored = new Set<string>()
        return pillarGroups(rows).map((group, index) => {
          const first = !anchored.has(group.pillar)
          anchored.add(group.pillar)
          return (
            <PillarSection
              key={`${group.pillar}-${index}`}
              pillar={group.pillar}
              metrics={group.metrics}
              cadence={cadence}
              query={query}
              anchorId={first ? `pillar-${cadence}-${group.pillar}` : undefined}
            />
          )
        })
      })()}
    </section>
  )
}
